// src/contexts/SharedEventsContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const SharedEventsContext = createContext();

export const SharedEventsProvider = ({ children }) => {
  // Store shared events
  const [sharedEvents, setSharedEvents] = useState([]);
  
  // Load initial data from localStorage if available
  useEffect(() => {
    try {
      const savedEvents = localStorage.getItem('sharedEvents');
      if (savedEvents) {
        setSharedEvents(JSON.parse(savedEvents));
      }
    } catch (err) {
      console.error('Error loading shared events from localStorage:', err);
    }
  }, []);
  
  // Save to localStorage when events change
  useEffect(() => {
    try {
      localStorage.setItem('sharedEvents', JSON.stringify(sharedEvents));
    } catch (err) {
      console.error('Error saving shared events to localStorage:', err);
    }
  }, [sharedEvents]);
  
  // Add shared event - core method for adding or updating an event
  const addSharedEvent = (event) => {
    console.log("Adding shared event:", event);
    
    setSharedEvents(prev => {
      // Check if the event already exists (based on id and robotId)
      const existingEventIndex = prev.findIndex(e => 
        (e.id && e.id === event.id) || 
        (e.robotId === event.robotId && e.startTime === event.startTime)
      );
      
      if (existingEventIndex !== -1) {
        // Event exists, update it
        return prev.map(e => {
          if ((e.id && e.id === event.id) || (e.robotId === event.robotId && e.startTime === event.startTime)) {
            // Handle savedTo as an array and merge with existing savedTo
            let newSavedTo = Array.isArray(e.savedTo) ? [...e.savedTo] : [e.savedTo || 'workspace'];
            if (event.savedTo) {
              if (Array.isArray(event.savedTo)) {
                event.savedTo.forEach(saveType => {
                  if (!newSavedTo.includes(saveType)) {
                    newSavedTo.push(saveType);
                  }
                });
              } else if (!newSavedTo.includes(event.savedTo)) {
                newSavedTo.push(event.savedTo);
              }
            }
            
            // Merge all properties, ensuring savedTo is properly handled
            return { 
              ...e, 
              ...event,
              savedTo: newSavedTo,
              messages: event.messages || e.messages,
              notes: event.notes || e.notes
            };
          }
          return e;
        });
      }
      
      // Ensure event has required fields
      const newEvent = {
        ...event,
        id: event.id || `${event.robotId}-${event.timeframe}-${event.startTime}-${Date.now()}`,
        timestamp: event.timestamp || new Date().toISOString(),
        status: event.status || 'pending',
        viewed: false, // Default to not viewed
        savedTo: Array.isArray(event.savedTo) ? event.savedTo : [event.savedTo || 'workspace'] // Initialize savedTo as array
      };
      
      console.log("Created new event:", newEvent);
      
      // Add new event to the existing events
      return [...prev, newEvent];
    });
  };
  
  // Remove shared event
  const removeSharedEvent = (id, robotId) => {
    console.log("Removing event:", { id, robotId });
    
    setSharedEvents(prev => 
      prev.filter(event => {
        // Filter by id (if any)
        if (id && event.id) {
          return event.id !== id;
        }
        // Fallback: filter by robotId, timeframe and startTime
        return !(event.robotId === robotId);
      })
    );
  };
  
  // Update shared event
  const updateSharedEvent = (id, updates) => {
    console.log("Updating event:", { id, updates });
    
    setSharedEvents(prev => 
      prev.map(event => {
        if (event.id === id) {
          return { ...event, ...updates };
        }
        return event;
      })
    );
  };
  
  // Add message to shared event
  const addMessageToEvent = (eventId, message) => {
    console.log("Adding message to event:", { eventId, message });
    
    setSharedEvents(prev => 
      prev.map(event => {
        if (event.id === eventId) {
          const newMessage = {
            id: Date.now().toString(),
            sender: 'Current Officer', // Replace with actual user
            message,
            timestamp: new Date().toISOString()
          };
          
          // Create or update messages array
          const messages = event.messages ? [...event.messages, newMessage] : [newMessage];
          
          return { ...event, messages, status: 'in-progress' };
        }
        return event;
      })
    );
  };
  
  // Change event status
  const changeEventStatus = (eventId, status) => {
    console.log("Changing event status:", { eventId, status });
    
    setSharedEvents(prev => 
      prev.map(event => {
        if (event.id === eventId) {
          return { ...event, status };
        }
        return event;
      })
    );
  };
  
  // Add to workspace - specific function for saving to personal workspace
  const addToWorkspace = (event, note) => {
    console.log("Adding to workspace:", event);
    
    // If note is provided as string, create note object
    if (note && typeof note === 'string' && note.trim()) {
      const noteObj = {
        id: Date.now().toString(),
        content: note,
        timestamp: new Date().toISOString(),
        isImportant: false,
        category: 'uncategorized'
      };
      
      if (!event.notes) {
        event.notes = [noteObj];
      } else {
        event.notes.push(noteObj);
      }
    }
    
    // If complete note object is provided, use it directly
    if (note && typeof note === 'object' && note.content) {
      if (!event.notes) {
        event.notes = [note];
      } else {
        event.notes.push(note);
      }
    }
    
    // Mark as saved to workspace, but preserve existing savedTo values
    const eventToSave = {
      ...event,
      timestamp: new Date().toISOString(),
      viewed: false // not viewed
    };

    // Initialize or update savedTo array
    if (!eventToSave.savedTo) {
      eventToSave.savedTo = ['workspace'];
    } else if (Array.isArray(eventToSave.savedTo)) {
      if (!eventToSave.savedTo.includes('workspace')) {
        eventToSave.savedTo.push('workspace');
      }
    } else {
      eventToSave.savedTo = [eventToSave.savedTo, 'workspace'];
    }
    
    // Use existing addSharedEvent function to handle duplicates
    addSharedEvent(eventToSave);
  };
  
  // Add to shared space - specific function for sharing with team
  const addToSharedSpace = (event, message) => {
    console.log("Adding to shared space:", event);
    
    // Ensure event has ID
    if (!event.id) {
      event.id = `${event.robotId}-${event.timeframe}-${event.startTime}-${Date.now()}`;
    }
    
    // If message is provided, create messages array
    if (message && typeof message === 'string' && message.trim()) {
      const messageObj = {
        id: Date.now().toString(),
        sender: 'Current Officer', // Replace with actual user
        message,
        timestamp: new Date().toISOString()
      };
      
      if (!event.messages) {
        event.messages = [messageObj];
      } else {
        event.messages.push(messageObj);
      }
    }
    
    // Mark as shared with team, but preserve existing savedTo values
    const eventToShare = {
      ...event,
      timestamp: new Date().toISOString(),
      viewed: false // not viewed
    };

    // Initialize or update savedTo array
    if (!eventToShare.savedTo) {
      eventToShare.savedTo = ['shared'];
    } else if (Array.isArray(eventToShare.savedTo)) {
      if (!eventToShare.savedTo.includes('shared')) {
        eventToShare.savedTo.push('shared');
      }
    } else {
      eventToShare.savedTo = [eventToShare.savedTo, 'shared'];
    }
    
    // Use existing addSharedEvent function to handle duplicates
    addSharedEvent(eventToShare);
  };
  
  // Mark event as viewed
  const markEventAsViewed = (eventId) => {
    console.log("Marking event as viewed:", eventId);
    
    setSharedEvents(prev => 
      prev.map(event => {
        if (event.id === eventId) {
          return { ...event, viewed: true };
        }
        return event;
      })
    );
  };

  return (
    <SharedEventsContext.Provider value={{ 
      sharedEvents,
      addSharedEvent,
      removeSharedEvent,
      updateSharedEvent,
      addMessageToEvent,
      changeEventStatus,
      addToWorkspace,
      addToSharedSpace,
      markEventAsViewed
    }}>
      {children}
    </SharedEventsContext.Provider>
  );
};

// Custom Hook for easy access in components
export const useSharedEvents = () => useContext(SharedEventsContext);